import { Chip, TableCell, TableRow } from '@mui/material';
import { FC, Fragment, Reducer, useCallback, useEffect, useReducer } from 'react';
// components
import { Alert } from 'components/common/Alert';
import TableLoader from 'components/common/TableLoader';
import NoDataFound from 'components/common/NoDataFound';
import TableContainer from 'components/common/TableContainer';
import TableComponent from 'components/common/TableComponent';
import TableSearchField from 'components/common/TableSearchField';
import ActionMenuDropdown from 'components/common/ActionMenuDropdown';
import TableTabsComponent from 'components/common/TableTabsComponent';
// constants, reducers, graphql, styles
import { useDebounce } from 'hooks/useDebounce';
import { textWhiteSpace } from 'styles/commonComponentStyle';
import { State, Action, ActionType, initialState, baseCoatingReducer } from 'reducer/baseCoatingReducer';
import {
  TABS,
  TabEnum,
  ACTIVE_TEXT,
  HTTP_STATUS,
  INACTIVE_TEXT,
  LOADING_TABLE_ROWS,
  EDIT_BASE_COATING_ROUTE,
  BASE_COATING_TABLE_HEADER,
} from 'constants/index';
import {
  TabletCoatingsPayload,
  useUpdateBaseCoatingMutation,
  useFindAllBaseCoatingTableLazyQuery,
} from 'generated/graphql';

const BaseCoatingTable: FC = () => {
  const [state, dispatch] = useReducer<Reducer<State, Action>>(baseCoatingReducer, initialState);
  const { page, rowsPerPage, search, count, data, activeTab } = state;
  const debounceValue = useDebounce(search);

  const [findAllBaseCoatings, { loading: fetchLoading, error }] = useFindAllBaseCoatingTableLazyQuery({
    fetchPolicy: 'network-only',
    nextFetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { findAllTabletCoating } = data;
      const { response, data: tabletCoatings, pagination } = findAllTabletCoating || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        const { page, totalCount } = pagination || {};
        dispatch({ type: ActionType.SET_PAGE, page: page || 1 });
        dispatch({ type: ActionType.SET_COUNT, count: totalCount || 0 });
        dispatch({
          type: ActionType.SET_DATA,
          data: tabletCoatings as TabletCoatingsPayload['data'],
        });
      } else {
        resetPage();
      }
    },

    onError: () => {
      resetPage();
    },
  });

  const [updateBaseCoatingStatus, { loading: updateLoading }] = useUpdateBaseCoatingMutation({
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,

    onCompleted: (data) => {
      const { updateTabletCoating } = data;
      const { response } = updateTabletCoating || {};
      const { message, status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        Alert.success(message || '');
        fetchAllBaseCoatings();
      } else {
        Alert.error(message ?? '');
      }
    },

    onError: ({ message }) => {
      Alert.error(message);
    },
  });

  const resetPage = () => {
    dispatch({ type: ActionType.SET_PAGE, page: 1 });
    dispatch({ type: ActionType.SET_COUNT, count: 0 });
    dispatch({ type: ActionType.SET_DATA, data: [] });
  };

  const fetchAllBaseCoatings = useCallback(async () => {
    await findAllBaseCoatings({
      variables: {
        findAllTabletCoatingInput: {
          paginationOptions: {
            limit: rowsPerPage,
            page,
          },
          search: debounceValue,
          ...(activeTab !== TabEnum.All && { isActive: activeTab === TabEnum.Active }),
        },
      },
    });
  }, [findAllBaseCoatings, debounceValue, rowsPerPage, page, activeTab]);

  useEffect(() => {
    fetchAllBaseCoatings();
  }, [fetchAllBaseCoatings]);

  const tabHandler = (_: React.SyntheticEvent<Element, Event>, value: string) => {
    dispatch({ type: ActionType.SET_ACTIVE_TAB, activeTab: value as TabEnum });
  };

  const onActiveHandler = async (id: string, status: boolean) => {
    await updateBaseCoatingStatus({
      variables: {
        updateTabletCoatingInput: {
          id,
          isActive: !status,
        },
      },
    });
  };

  const loading = fetchLoading || updateLoading;

  const noData = Boolean((!fetchLoading && data?.length === 0) || error);

  return (
    <TableContainer>
      <TableTabsComponent activeTab={activeTab} tabsList={TABS} tabHandler={tabHandler} />

      <TableSearchField
        search={search}
        onClear={() => dispatch({ type: ActionType.SET_SEARCH, search: '' })}
        onChange={(val) => dispatch({ type: ActionType.SET_SEARCH, search: val })}
      />

      <TableComponent
        noData={noData}
        page={page - 1}
        count={count}
        rowsPerPage={rowsPerPage}
        tableHeader={BASE_COATING_TABLE_HEADER}
        setPage={(p: number) => dispatch({ type: ActionType.SET_PAGE, page: p + 1 })}
        setRowsPerPage={(r: number) => dispatch({ type: ActionType.SET_ROWS_PER_PAGE, rowsPerPage: r })}>
        {loading ? (
          <TableLoader columns={6} rows={LOADING_TABLE_ROWS} />
        ) : (
          <Fragment>
            {data?.map((cell) => {
              const { id, name, isActive, costPerKg, loadAdd, productType } = cell || {};
              const { name: productTypeName } = productType || {};
              return (
                <TableRow key={id}>
                  <TableCell sx={textWhiteSpace}>{name ?? '--'}</TableCell>
                  <TableCell>{productTypeName || '--'}</TableCell>
                  <TableCell>{costPerKg || '--'}</TableCell>
                  <TableCell>{loadAdd || '--'}</TableCell>

                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={isActive ? ACTIVE_TEXT : INACTIVE_TEXT}
                      color={isActive ? 'success' : 'error'}
                    />
                  </TableCell>

                  <TableCell>
                    <ActionMenuDropdown
                      id={id ?? ''}
                      isActive={!!isActive}
                      editRoute={EDIT_BASE_COATING_ROUTE}
                      onActiveHandler={onActiveHandler}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </Fragment>
        )}
      </TableComponent>

      <NoDataFound noData={noData} />
    </TableContainer>
  );
};

export default BaseCoatingTable;
